/**
 * Prompt Command
 * Quick shortcut for executing tasks via the main agent
 * The LLM will analyze the prompt and decide how to execute (chain, batch, or direct)
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { existsSync, statSync } from 'fs';
import { checkAuthentication, initializeAgent } from '../agent/utils.js';
import { createAgentAnalyzingSpinner } from '../agent/spinner.js';

export const promptCommand = new Command('prompt')
    .description('Execute a task with intelligent routing (LLM decides chain, batch, or direct)')
    .argument('[prompt...]', 'Task prompt')
    .option('--no-mcp', 'Disable local MCP server integration')
    .option('-d, --debug', 'Enable debug logging')
    .option('-w, --workspace <path>', 'Working directory (passed to agent context)')
    .action(async (promptArgs: string[], options) => {
        checkAuthentication();

        const promptText = promptArgs.join(' ').trim();
        if (!promptText) {
            console.log(chalk.red('❌ Error: No prompt provided.'));
            console.log(chalk.dim('Usage: paean prompt "Your task description"'));
            console.log('');
            console.log(chalk.dim('Examples:'));
            console.log(chalk.dim('  paean prompt "分析当前项目并生成报告"'));
            console.log(chalk.dim('  paean prompt "先分析再计划再执行"  # LLM will auto-detect chain intent'));
            console.log(chalk.dim('  paean prompt "创建5个测试文件"      # LLM will auto-detect batch intent'));
            process.exit(1);
        }

        // Validate prompt length
        if (promptText.length > 10000) {
            console.log(chalk.red('❌ Error: Prompt is too long (max 10000 characters).'));
            process.exit(1);
        }

        if (promptText.length < 3) {
            console.log(chalk.red('❌ Error: Prompt is too short (min 3 characters).'));
            process.exit(1);
        }

        const debug = options.debug ?? false;
        const enableMcp = options.mcp !== false;
        const workspace = options.workspace || process.cwd();

        // Validate workspace path
        if (options.workspace) {
            if (!existsSync(workspace)) {
                console.log(chalk.red(`❌ Error: Workspace path does not exist: ${workspace}`));
                process.exit(1);
            }
            try {
                if (!statSync(workspace).isDirectory()) {
                    console.log(chalk.red(`❌ Error: Workspace path is not a directory: ${workspace}`));
                    process.exit(1);
                }
            } catch {
                console.log(chalk.red(`❌ Error: Cannot access workspace path: ${workspace}`));
                process.exit(1);
            }
        }

        // Header with proper alignment - use fixed-width box
        console.log('');
        console.log(chalk.bold.cyan('  ╔═══════════════════════════════════════════╗'));
        console.log(chalk.bold.cyan('  ║') + '     PAEAN PROMPT ' + chalk.magenta('◆') + ' Agent Mode          ' + chalk.bold.cyan('║'));
        console.log(chalk.bold.cyan('  ╚═══════════════════════════════════════════╝'));
        console.log('');

        console.log(chalk.cyan('▶') + chalk.bold(' Prompt:'));
        console.log(chalk.dim(`  ${promptText.length > 70 ? promptText.slice(0, 70) + '...' : promptText}`));
        console.log('');

        // Enhance prompt with workspace context
        const enhancedPrompt = workspace !== process.cwd()
            ? `[Working directory: ${workspace}]\n\n${promptText}`
            : promptText;

        // Initialize agent with MCP tools (including worker tools)
        const { mcpState, onMcpToolCall, mcpClient } = await initializeAgent({
            mcp: enableMcp,
            debug,
        });

        // Show MCP status
        const mcpToolCount = mcpState?.mcpServers?.reduce(
            (sum, server) => sum + (server.tools?.length || 0),
            0
        ) || 0;

        if (mcpToolCount > 0) {
            console.log(chalk.green('✓') + ` MCP enabled with ${chalk.bold(mcpToolCount)} tools`);
            if (debug) {
                for (const server of mcpState?.mcpServers || []) {
                    console.log(chalk.dim(`  [${server.name}] ${server.tools?.length || 0} tools`));
                }
            }
        } else if (enableMcp) {
            console.log(chalk.yellow('⚠') + ' No MCP tools available');
        }
        console.log('');

        // Create animated spinner for agent analysis
        const spinner = createAgentAnalyzingSpinner();

        try {
            spinner.start();
            console.log(chalk.dim('  (LLM will decide: direct execution, chain tasks, or batch tasks)'));
            console.log('');

            // Use sendMessage to let LLM process and decide
            const { sendMessage } = await import('../agent/chat.ink.js');

            const response = await sendMessage(enhancedPrompt, {
                mcpState,
                onMcpToolCall,
                debug,
            });

            spinner.success('∩ Analysis complete');
            console.log('');
            console.log(chalk.bold('📝 Agent Response:'));
            console.log(response);
            console.log('');

        } catch (error) {
            spinner.fail('Analysis failed');
            const errorMessage = error instanceof Error 
                ? error.message 
                : 'Unknown error occurred';
            console.error(chalk.red(`\n❌ Error: ${errorMessage}`));
            if (debug) {
                console.error(chalk.dim(`\nStack trace:\n${error instanceof Error ? error.stack : ''}`));
            }
            process.exit(1);
        } finally {
            try {
                await mcpClient?.disconnectAll();
            } catch (cleanupError) {
                if (debug) {
                    console.error(chalk.dim(`Warning: Error during cleanup: ${(cleanupError as Error).message}`));
                }
            }
        }
    });
